/**
 * Error Groups Service
 * API service for managing error groups
 */
import { api } from './api';

// Types
export interface ErrorGroup {
  id: number;
  error_type: string;
  sku: string | null;
  status: 'pending' | 'processing' | 'resolved' | 'failed' | 'ignored';
  affected_combos_count: number;
  combo_ids?: string[];
  reason: string;
  reprocessable: boolean;
  first_occurrence: string;
  last_occurrence: string;
  last_reprocess_attempt: string | null;
  reprocess_attempts: number;
  country_code: string;
  country_name: string;
}

export interface ComboErrorDetail {
  id: number;
  combo_id: string;
  sku: string | null;
  reason: string;
  detail: string;
  error_code: string;
  stack_trace: string;
  payload: any;
  created_at: string;
}

export interface ErrorGroupListParams {
  page?: number;
  page_size?: number;
  status?: string;
  error_type?: string;
  reprocessable?: boolean;
  search?: string;
  ordering?: string;
}

export interface ErrorGroupListResponse {
  count: number;
  next: string | null;
  previous: string | null;
  results: ErrorGroup[];
}

export interface ComboErrorListResponse {
  count: number;
  next: string | null;
  previous: string | null;
  results: ComboErrorDetail[];
}

export interface ReprocessRequest {
  error_group_ids?: number[];
  error_type?: string;
  sku?: string;
  reprocess_all?: boolean;
}

export interface ReprocessResponse {
  message: string;
  tasks?: Array<{
    error_group_id: number;
    task_id: string;
  }>;
  task_id?: string;
}

// Service methods
const errorGroupsService = {
  /**
   * Get list of error groups with optional filters
   */
  getErrorGroups: async (params?: ErrorGroupListParams): Promise<ErrorGroupListResponse> => {
    const response = await api.get('/error-groups/', { params });
    return response.data;
  },

  /**
   * Get detail of a specific error group
   */
  getErrorGroupDetail: async (id: number): Promise<ErrorGroup> => {
    const response = await api.get(`/error-groups/${id}/`);
    return response.data;
  },

  /**
   * Get list of combo error logs for a specific error group
   */
  getErrorGroupCombos: async (
    id: number,
    params?: { page?: number; page_size?: number }
  ): Promise<ComboErrorListResponse> => {
    const response = await api.get(`/error-groups/${id}/combos/`, { params });
    return response.data;
  },

  /**
   * Reprocess errors
   * Supports 4 modes: by IDs, by error type, by SKU, or all pending
   */
  reprocessErrors: async (request: ReprocessRequest): Promise<ReprocessResponse> => {
    const response = await api.post('/error-groups/reprocess/', request);
    return response.data;
  },

  /**
   * Get error groups statistics
   */
  getErrorGroupsStats: async (): Promise<{
    total: number;
    by_status: Record<string, number>;
    by_type: Record<string, number>;
  }> => {
    // Get first page with all statuses to calculate stats
    const response = await api.get('/error-groups/', {
      params: { page_size: 1 }
    });

    // Calculate stats from count
    return {
      total: response.data.count,
      by_status: {},
      by_type: {}
    };
  }
};

export default errorGroupsService;
